import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  assetsSavedSearchDestroy,
  assetsSavedSearchList,
  PaginatedSavedSearchesList,
  SavedSearches,
  assetsHybridSearchRelevantSearchSourcesV1Retrieve,
  assetsHybridSearchCreate,
  RelevantSearchSourcesResponse
} from '@goldcast/api/content';
import { AI_SEARCH_ERROR_CODES, SELECTION_ROUTE_MAPPING } from '../constants';
import { AiClipPromptOption } from '../types';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast } from '@/libs/toast/toast';
import useAnalytics from '@/hooks/useAnalytics';
import { currentUser } from '@/stores/user';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';

export function useAiSearchApi() {
  const [savedSearches, setSavedSearches] = useState<SavedSearches[]>([]);
  const [loadingRelevantSources, setLoadingRelevantSources] = useState(false);
  const [relevantSourceList, setRelevantSourceList] = useState<RelevantSearchSourcesResponse>({
    contents: [],
    search_term: '',
    metadata: {}
  });

  const navigate = useNavigate();
  const { adminAppStore, logger } = useAppContext();
  const { trackAiSearch, trackAiSearchFailure } = useAnalytics();

  // Load saved searches
  const loadSavedSearches = useCallback(
    (searchTerm?: string) => {
      return assetsSavedSearchList({
        queryParams: {
          limit: 10,
          offset: 0,
          ordering: '-created_at',
          title: searchTerm,
          organization: currentUser.getSnapshot()?.organization as string,
          exclude_homepage: true,
          workspace_filter: true
        }
      })
        .then((resp: PaginatedSavedSearchesList) => {
          setSavedSearches(resp.results || []);
          return resp.results || [];
        })
        .catch(err => {
          logger.error('[useAiSearchApi] Failed to load saved searches', getErrorDiagnostics(err));
          throw err;
        });
    },
    [logger]
  );

  // Delete saved search
  const deleteSearch = useCallback(
    async (savedSearchId: string) => {
      try {
        await assetsSavedSearchDestroy({ id: savedSearchId });
        setSavedSearches(list => list.filter(item => item.id !== savedSearchId));
        return true;
      } catch (err) {
        logger.error('[useAiSearchApi] Failed to delete saved search', {
          savedSearchId,
          ...getErrorDiagnostics(err)
        });
        showErrorToast("Whoops! Deleting the Saved Search didn't go as planned. Check your connection and try again.");
        return false;
      }
    },
    [logger]
  );

  // Get relevant search sources
  const getRelevantSearchSources = useCallback((prompt: string, selectedPromptOption: AiClipPromptOption) => {
    setLoadingRelevantSources(true);

    const searchParams = {
      organization: currentUser.getSnapshot()?.organization as string,
      search_term: prompt,
      generation_type: selectedPromptOption.type
    };

    return assetsHybridSearchRelevantSearchSourcesV1Retrieve({
      queryParams: {
        ...searchParams
      }
    })
      .then((sourceList: any) => {
        setRelevantSourceList(sourceList);
        return sourceList;
      })
      .catch(err => {
        if (err && err.toString() === AI_SEARCH_ERROR_CODES.RELEVANT_SOURCES_NOT_FOUND) {
          throw new Error('NO_RESULTS');
        }
        throw err;
      })
      .finally(() => {
        setLoadingRelevantSources(false);
      });
  }, []);

  // Create AI search
  const createAiSearch = useCallback(
    (prompt: string, selectedPromptOption: AiClipPromptOption, selectedSource?: string[]) => {
      const searchRequestBody = {
        organization_id: adminAppStore.user?.organization as string,
        search_term: relevantSourceList?.search_term,
        title: prompt,
        generation_type: selectedPromptOption.type,
        selected_sources: selectedSource,
        metadata: {
          ...relevantSourceList?.metadata,
          original_query: prompt
        }
      };

      trackAiSearch({
        term: prompt,
        contentType: selectedPromptOption.type
      });

      return assetsHybridSearchCreate({ body: searchRequestBody })
        .then(res => {
          navigate(`/search/${res.id}/${SELECTION_ROUTE_MAPPING[selectedPromptOption.type]}`);
          return res;
        })
        .catch(err => {
          trackAiSearchFailure(err ? err.toString() : '', prompt);

          try {
            if (err && err.toString() === AI_SEARCH_ERROR_CODES.CLIP_NOT_EXIST_FOR_SEARCH_TERM) {
              throw new Error('NO_RESULTS');
            } else {
              logger.error('[useAiSearchApi] Failed to create saved search', getErrorDiagnostics(err));
              showErrorToast('Something went wrong while generating clips. Please check your search and try again.');
              throw err;
            }
          } catch (error) {
            logger.error('[useAiSearchApi] Failed to process saved search creation error', {
              ...getErrorDiagnostics(error)
            });
            showErrorToast('Something went wrong while generating clips. Please check your search and try again.');
            throw err;
          }
        });
    },
    [relevantSourceList, adminAppStore.user?.organization, trackAiSearch, navigate, trackAiSearchFailure, logger]
  );

  return {
    // State
    savedSearches,
    loadingRelevantSources,
    relevantSourceList,

    // API methods
    loadSavedSearches,
    deleteSearch,
    getRelevantSearchSources,
    createAiSearch
  };
}
